import React, { useEffect, useState } from 'react';
import { speak } from '../services/audio';
import { Content, lookupWords } from '../services/api';
import './WordCard.css';

// Common word dictionary for sentence breakdowns
const wordDictionary: Record<string, string> = {
  // Articles & Determiners
  'the': '', 'a': '', 'an': '', 'this': 'to/ten/ta', 'that': 'tamto/tamten/tamta',
  'my': 'mój/moja', 'your': 'twój/twoja', 'his': 'jego', 'her': 'jej', 'its': 'jego/jej',
  'our': 'nasz/nasza', 'their': 'ich', 'some': 'trochę/jakieś', 'any': 'jakikolwiek',
  // Pronouns
  'I': 'ja', 'you': 'ty/wy', 'he': 'on', 'she': 'ona', 'it': 'to/ono', 'we': 'my', 'they': 'oni/one',
  'me': 'mnie/mi', 'him': 'jego/jemu', 'us': 'nas/nam', 'them': 'ich/im',
  // Verbs - be
  'am': 'jestem', 'is': 'jest', 'are': 'jesteś/jesteśmy/są', 'was': 'był/była', 'were': 'byli/były',
  'be': 'być', 'been': 'był/była',
  // Verbs - have
  'have': 'mam/masz/mają', 'has': 'ma', 'had': 'miał/miała',
  // Verbs - do
  'do': 'robić/robię', 'does': 'robi', 'did': 'zrobił/zrobiła', 'done': 'zrobione',
  // Modal verbs
  'can': 'mogę/możesz', 'could': 'mógłbym/mogłabym', 'will': 'będę', 'would': 'bym',
  'should': 'powinienem', 'must': 'muszę', 'may': 'mogę',
  // Common verbs
  'go': 'iść/jechać', 'goes': 'idzie/jedzie', 'going': 'idący/jadący', 'went': 'poszedł/pojechał',
  'come': 'przyjść', 'comes': 'przychodzi', 'coming': 'przychodzący', 'came': 'przyszedł',
  'see': 'widzieć', 'sees': 'widzi', 'saw': 'widział', 'seen': 'widziany',
  'look': 'patrzeć/spójrz', 'looks': 'patrzy/wygląda', 'looking': 'patrzący',
  'get': 'dostać/wziąć', 'gets': 'dostaje', 'got': 'dostał',
  'give': 'dać', 'gives': 'daje', 'gave': 'dał', 'given': 'dany',
  'take': 'wziąć', 'takes': 'bierze', 'took': 'wziął', 'taken': 'wzięty',
  'make': 'robić/zrobić', 'makes': 'robi', 'made': 'zrobił/zrobiony',
  'know': 'wiedzieć/znać', 'knows': 'wie/zna', 'knew': 'wiedział/znał',
  'think': 'myśleć', 'thinks': 'myśli', 'thought': 'myślał/myśl',
  'want': 'chcieć', 'wants': 'chce', 'wanted': 'chciał',
  'like': 'lubić', 'likes': 'lubi', 'liked': 'lubił',
  'love': 'kochać', 'loves': 'kocha', 'loved': 'kochał',
  'need': 'potrzebować', 'needs': 'potrzebuje', 'needed': 'potrzebował',
  'eat': 'jeść', 'eats': 'je', 'ate': 'jadł', 'eating': 'jedzący',
  'drink': 'pić', 'drinks': 'pije', 'drank': 'pił',
  'sleep': 'spać', 'sleeps': 'śpi', 'slept': 'spał',
  'play': 'grać/bawić się', 'plays': 'gra', 'played': 'grał',
  'run': 'biegać', 'runs': 'biega', 'ran': 'biegł', 'running': 'biegnący',
  'walk': 'chodzić/spacerować', 'walks': 'chodzi', 'walked': 'chodził',
  'sit': 'siedzieć', 'sits': 'siedzi', 'sat': 'siedział',
  'stand': 'stać', 'stands': 'stoi', 'stood': 'stał',
  'put': 'położyć/kłaść', 'puts': 'kładzie',
  'read': 'czytać', 'reads': 'czyta',
  'write': 'pisać', 'writes': 'pisze', 'wrote': 'pisał', 'written': 'napisany',
  'say': 'powiedzieć', 'says': 'mówi', 'said': 'powiedział',
  'tell': 'powiedzieć/opowiedzieć', 'tells': 'mówi', 'told': 'powiedział',
  'ask': 'pytać/prosić', 'asks': 'pyta', 'asked': 'pytał',
  'help': 'pomagać/pomóc', 'helps': 'pomaga', 'helped': 'pomógł',
  'open': 'otwierać/otwarty', 'opens': 'otwiera', 'opened': 'otworzył',
  'close': 'zamykać/zamknij', 'closes': 'zamyka', 'closed': 'zamknął/zamknięty',
  'wait': 'czekać', 'waits': 'czeka', 'waiting': 'czekający',
  'hurry': 'spieszyć się', 'bring': 'przynosić', 'brings': 'przynosi',
  // Prepositions
  'in': 'w', 'on': 'na', 'at': 'przy/w/o', 'to': 'do', 'for': 'dla/na',
  'with': 'z', 'from': 'z/od', 'of': '', 'by': 'przez/przy', 'about': 'o',
  'under': 'pod', 'over': 'nad/ponad', 'above': 'nad', 'below': 'poniżej/pod',
  'behind': 'za', 'between': 'między', 'next': 'następny/obok',
  'near': 'blisko', 'up': 'w górę', 'down': 'w dół', 'out': 'na zewnątrz',
  'into': 'do/w', 'front': 'przód',
  // Question words
  'what': 'co', 'where': 'gdzie', 'when': 'kiedy', 'why': 'dlaczego',
  'how': 'jak', 'who': 'kto', 'which': 'który',
  // Conjunctions
  'and': 'i', 'or': 'lub', 'but': 'ale', 'because': 'ponieważ', 'if': 'jeśli',
  'so': 'więc', 'then': 'wtedy/potem',
  // Adjectives
  'good': 'dobry', 'bad': 'zły', 'big': 'duży', 'small': 'mały', 'little': 'mały',
  'new': 'nowy', 'old': 'stary', 'young': 'młody', 'long': 'długi', 'short': 'krótki',
  'tall': 'wysoki', 'hot': 'gorący', 'cold': 'zimny', 'warm': 'ciepły',
  'happy': 'szczęśliwy', 'sad': 'smutny', 'nice': 'miły', 'beautiful': 'piękny',
  'ready': 'gotowy', 'hungry': 'głodny', 'thirsty': 'spragniony', 'tired': 'zmęczony',
  'fast': 'szybki', 'slow': 'wolny', 'easy': 'łatwy', 'hard': 'trudny',
  'clean': 'czysty', 'dirty': 'brudny', 'quiet': 'cichy', 'loud': 'głośny',
  // Adverbs
  'here': 'tutaj', 'there': 'tam', 'now': 'teraz', 'today': 'dzisiaj',
  'tomorrow': 'jutro', 'yesterday': 'wczoraj', 'always': 'zawsze', 'never': 'nigdy',
  'sometimes': 'czasami', 'often': 'często', 'very': 'bardzo', 'really': 'naprawdę',
  'just': 'tylko/właśnie', 'also': 'też/również', 'too': 'też/za', 'only': 'tylko',
  'later': 'później', 'soon': 'wkrótce', 'outside': 'na zewnątrz', 'inside': 'w środku',
  // Nouns - Family
  'mother': 'mama', 'father': 'tata', 'mom': 'mama', 'dad': 'tata',
  'sister': 'siostra', 'brother': 'brat', 'baby': 'dziecko',
  'grandmother': 'babcia', 'grandfather': 'dziadek', 'family': 'rodzina',
  // Nouns - Animals
  'cat': 'kot', 'dog': 'pies', 'bird': 'ptak', 'fish': 'ryba', 'mouse': 'mysz',
  // Nouns - Objects
  'book': 'książka', 'books': 'książki', 'table': 'stół', 'chair': 'krzesło',
  'door': 'drzwi', 'window': 'okno', 'bed': 'łóżko', 'desk': 'biurko',
  'box': 'pudełko', 'ball': 'piłka', 'toy': 'zabawka', 'lamp': 'lampa',
  'picture': 'obrazek', 'wall': 'ściana', 'floor': 'podłoga', 'shelf': 'półka',
  'pillow': 'poduszka', 'sofa': 'kanapa', 'bag': 'torba', 'plate': 'talerz',
  'clock': 'zegar', 'rug': 'dywan', 'vase': 'wazon', 'shoes': 'buty',
  'flowers': 'kwiaty', 'tree': 'drzewo',
  // Nouns - Places
  'house': 'dom', 'home': 'dom', 'school': 'szkoła', 'park': 'park',
  'room': 'pokój', 'shop': 'sklep', 'left': 'lewo', 'right': 'prawo',
  // Nouns - Food
  'apple': 'jabłko', 'water': 'woda', 'milk': 'mleko', 'bread': 'chleb',
  'food': 'jedzenie', 'breakfast': 'śniadanie', 'lunch': 'obiad', 'dinner': 'kolacja',
  // Nouns - Time
  'time': 'czas', 'day': 'dzień', 'night': 'noc', 'morning': 'rano',
  'afternoon': 'popołudnie', 'evening': 'wieczór', 'week': 'tydzień',
  'moment': 'chwila', 'minute': 'minuta', 'hour': 'godzina',
  'Monday': 'poniedziałek', 'Tuesday': 'wtorek', 'Wednesday': 'środa',
  'Thursday': 'czwartek', 'Friday': 'piątek', 'Saturday': 'sobota', 'Sunday': 'niedziela',
  // Nouns - Body
  'hand': 'ręka', 'hands': 'ręce', 'head': 'głowa', 'eye': 'oko', 'eyes': 'oczy',
  // Nouns - Other
  'name': 'imię', 'thing': 'rzecz', 'things': 'rzeczy', 'way': 'droga/sposób',
  'car': 'samochód', 'friend': 'przyjaciel', 'friends': 'przyjaciele',
  // Numbers
  'one': 'jeden', 'two': 'dwa', 'three': 'trzy', 'four': 'cztery', 'five': 'pięć',
  // Other common words
  'yes': 'tak', 'no': 'nie', 'not': 'nie', 'please': 'proszę', 'thank': 'dziękuję',
  'sorry': 'przepraszam', 'hello': 'cześć', 'hi': 'hej', 'bye': 'pa', 'goodbye': 'do widzenia',
  'okay': 'dobrze', 'ok': 'ok', 'well': 'dobrze/cóż', 'let': 'pozwól',
  'more': 'więcej', 'much': 'dużo', 'many': 'wiele', 'all': 'wszystko/wszyscy',
  'every': 'każdy', 'other': 'inny', 'same': 'taki sam', 'different': 'inny',
  // Christmas
  'Christmas': 'Boże Narodzenie', 'Santa': 'Mikołaj', 'Merry': 'Wesołych',
  'Happy': 'Szczęśliwego', 'Year': 'Rok', 'New': 'Nowy', 'presents': 'prezenty',
  'snowing': 'pada śnieg',
};

// Get word translation from hardcoded dictionary, handling punctuation
const getWordTranslation = (word: string, dbTranslations: Record<string, string> = {}): string | null => {
  const cleanWord = word.replace(/[.,!?;:'"()]/g, '').toLowerCase();
  const originalClean = word.replace(/[.,!?;:'"()]/g, '');

  // Try hardcoded dictionary first (has richer translations like "twój/twoja")
  if (wordDictionary[originalClean]) return wordDictionary[originalClean];
  if (wordDictionary[cleanWord]) return wordDictionary[cleanWord];

  // Fall back to database translations
  if (dbTranslations[cleanWord]) return dbTranslations[cleanWord];

  return null;
};

interface WordCardProps {
  content: Content;
  showPolish?: boolean;
  onFlip?: () => void;
  size?: 'normal' | 'large';
}

const WordCard: React.FC<WordCardProps> = ({
  content,
  showPolish = true,
  onFlip,
  size = 'normal'
}) => {
  const [dbTranslations, setDbTranslations] = useState<Record<string, string>>({});

  // Check if content is a sentence (has spaces)
  const isSentence = content.english.includes(' ');

  // Fetch missing translations from database
  useEffect(() => {
    if (!isSentence || !showPolish) return;

    const words = content.english.split(' ').map(w => w.replace(/[.,!?;:'"()]/g, '').toLowerCase());
    const missingWords = words.filter(w => !wordDictionary[w] && wordDictionary[w] !== '');

    if (missingWords.length === 0) return;

    lookupWords(missingWords).then(setDbTranslations).catch(() => {});
  }, [content.english, isSentence, showPolish]);

  const handleSpeak = (e: React.MouseEvent) => {
    e.stopPropagation();
    speak(content.english);
  };

  // Build word-by-word dictionary for sentences
  const wordBreakdown = isSentence ? content.english.split(' ').map(word => ({
    word,
    translation: getWordTranslation(word, dbTranslations)
  })).filter(item => item.translation && item.translation !== '') : [];

  return (
    <div className={`word-card ${size}`} onClick={onFlip}>
      <div className="word-english">{content.english}</div>
      {showPolish && <div className="word-polish">{content.polish}</div>}

      {/* Word-by-word dictionary for sentences */}
      {showPolish && isSentence && wordBreakdown.length > 0 && (
        <div className="word-breakdown">
          <div className="breakdown-title">📖 Słowniczek:</div>
          <div className="breakdown-words">
            {wordBreakdown.map((item, idx) => (
              <span key={idx} className="breakdown-item">
                <span className="breakdown-en">{item.word.replace(/[.,!?;:'"()]/g, '')}</span>
                <span className="breakdown-pl">{item.translation}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      <button className="speak-btn" onClick={handleSpeak} title="Posłuchaj">
        🔊
      </button>
      {content.type === 'sentence' && (
        <span className="type-badge">zdanie</span>
      )}
    </div>
  );
};

export default WordCard;

